import React, { useContext, useMemo, useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Instructor } from "@models/ISchema";
import { AppContext } from "../../../context/EditSessionsContext";
import { Types } from "../../../context/switchPage.reducer";
import { Types as FormDataTypes } from "../../../context/formData.reducer";
import { ContextApp } from "@config/Context/contextApp";
import FormItem from "../../../components/FormEdit/FormItem";
import InstructorPicker from "../../../components/instructor-picker";
import { useIsEditorLocked } from "../../../hooks/useIsEditorLocked";
import useInstructorSearch, { InstructorOption } from "../../../hooks/useInstructorSearch";
import styles from "./instructorSection.module.scss";

interface InstructorSectionProps {
  disabled?: boolean;
}

const InstructorSection = ({ disabled: isDisabledByAbility = false }: InstructorSectionProps) => {
  const { state, dispatch } = useContext(AppContext);
  const isLocked = useIsEditorLocked();
  // The picker follows the lock too, but the permissions message only explains missing abilities.
  const disabled = isDisabledByAbility || isLocked;
  const { user } = useContext(ContextApp);
  const [isReadOnly, setIsReadOnly] = useState(!state?.form?.isEditionEnabled?.["instructors"]);
  const { t } = useTranslation();

  const shouldFilterInstructors = state?.page?.active?.advanceSearchFilterInstructor;

  const { options, loading, onSearch } = useInstructorSearch({
    courseId: state?.link?.info?.course?.id,
    shouldFilter: shouldFilterInstructors,
  });

  const someDeletedSessionIsSelected = useMemo(() => {
    return state?.form?.selectedSessions
      .map(session => session.id || "-")
      .some(id => state?.form?.sessionsToDelete.hasOwnProperty(id));
  }, [state?.form?.selectedSessions, state?.form?.sessionsToDelete]);

  const handleFilterChange = (checked: boolean) => {
    dispatch({
      type: Types.SetFilterInstructors,
      payload: checked,
    });
  };

  const handleSelect = (instructor: InstructorOption) => {
    const alreadyExists = state?.form?.editedSessions?.instructors?.find(
      item => item.id === instructor.value,
    );

    if (!alreadyExists) {
      const instructorsList = state?.form?.editedSessions?.instructors
        ? [...state?.form?.editedSessions?.instructors]
        : [];
      instructorsList.push(instructor);

      dispatch({
        type: FormDataTypes.InstructorEditedSessions,
        payload: { instructors: instructorsList },
      });
    }
  };

  const handleDelete = (instructor: Instructor) => {
    const listInstructors = [...state?.form?.editedSessions?.instructors];
    const indexInstructor = listInstructors.findIndex(item => item.id === instructor?.id);
    listInstructors.splice(indexInstructor, 1);

    dispatch({
      type: FormDataTypes.InstructorEditedSessions,
      payload: { instructors: listInstructors },
    });
  };

  const handleAdvancedSearch = () => {
    dispatch({
      type: Types.AdvanceSearchInstructor,
      payload: true,
    });
  };

  return (
    <FormItem
      title={t("assignation-edit.form.instructor.title")}
      type="instructors"
      isDisabled={isDisabledByAbility}
      onShowReadOnlyContent={setIsReadOnly}
    >
      <motion.div
        className={styles.instructor}
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ type: "spring", duration: 0.3, bounce: 0.25 }}
      >
        <InstructorPicker
          instructors={state?.form?.editedSessions?.instructors ?? []}
          disabled={disabled}
          loading={loading}
          options={options}
          shouldFilterInstructors={shouldFilterInstructors}
          canAssignFromOtherCourses={!!user?.abilities?.can_assign_instructors_from_other_courses}
          someDeletedSessionIsSelected={someDeletedSessionIsSelected}
          isReadOnly={isReadOnly}
          onSearch={onSearch}
          onFilterChange={handleFilterChange}
          onSelect={handleSelect}
          onDelete={handleDelete}
          onAdvancedSearch={handleAdvancedSearch}
        />
      </motion.div>
    </FormItem>
  );
};

export default InstructorSection;
